/* eslint-disable camelcase */
/* eslint-disable valid-jsdoc */
import { AccordionNode } from 'application/ui/accordion'
import { PhoneInputController } from 'application/ui/fields/phone'
import type { Quest, Slot } from './booking/types'
import {
  BookingStateManager,
  getRequiredBookingServices
} from './booking/state'
import { BookingAPI } from './booking/api'
import { BookingValidator } from './booking/validation'
import { BookingUIUpdater } from './booking/ui-updaters'
import {
  BookingEventHandlers,
  parseQuestParams
} from './booking/event-handlers'
import Modals from 'application/ui/modal/modal'
import { reachGoal } from 'application/utils/analytics'

/**
 * Главный контроллер модалки бронирования
 * Координирует работу всех подмодулей
 */
export class BookingModalController {
  private modal: HTMLElement
  private modalName: string = 'booking_modal'
  private timetable: HTMLElement | null = null
  private phoneController: PhoneInputController | null = null

  // Подмодули
  private stateManager!: BookingStateManager
  private api!: BookingAPI
  private validator!: BookingValidator
  private uiUpdater!: BookingUIUpdater
  private eventHandlers!: BookingEventHandlers

  constructor() {
    this.init()
  }

  /**
   * Инициализация контроллера
   */
  private init(): void {
    this.modal = document.querySelector<HTMLElement>(
      `[data-modal-name="${this.modalName}"]`
    ) as HTMLElement

    this.timetable = document.querySelector<HTMLElement>('[data-timetable]')

    // Инициализация подмодулей
    this.stateManager = new BookingStateManager()
    this.api = new BookingAPI()
    this.validator = new BookingValidator(this.modal)
    this.uiUpdater = new BookingUIUpdater(this.modal, this.stateManager)
    this.eventHandlers = new BookingEventHandlers(
      this.modal,
      this.timetable,
      this.stateManager,
      this.uiUpdater,
      this.api,
      this.validator,
      this.phoneController,
      () => this.closeBooking(),
      () => this.onBookingCreated(),
      (quest: Quest, slot: Slot) => this.open(quest, slot)
    )

    Modals.getInstance().addModal(
      this.modalName,
      {
        closeCallback: () => this.closeBooking(),
        disableUserClose: true,
        onTriedClosing: () => this.eventHandlers.closeModalHandler()
      },
      true
    )

    // Привязать события
    this.eventHandlers.bindAll()
    this.bindTicketSystemButtons()
  }

  /**
   * Кнопки «Забронировать» для билетной системы (вне #timetableSection —
   * сайдбар/мобильная навигация/карточка каталога) открывают попап напрямую,
   * без клика по слоту расписания.
   */
  private bindTicketSystemButtons(): void {
    document
      .querySelectorAll<HTMLElement>('[data-open-ticket-booking]')
      .forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault()

          const quest = parseQuestParams(
            button.getAttribute('data-quest-params') || '{}'
          )
          this.openTicketSystem(quest)
        })
      })
  }

  /**
   * Callback после создания бронирования
   */
  private onBookingCreated(): void {
    // Обновить UI
    this.uiUpdater.updateAll()
  }

  /**
   * ОТКРЫТИЕ МОДАЛКИ
   * Вызывается при клике на слот времени
   */
  public open(quest: Quest, slot: Slot): void {
    if (slot.booked) return

    reachGoal('new_TimeslotClick')

    // Сначала устанавливаем quest и slot
    this.stateManager.setState({
      quest,
      slot
    })
    const requiredServices = getRequiredBookingServices(
      this.stateManager.getState()
    )

    // Затем устанавливаем остальные параметры
    this.stateManager.setState({
      time: Date.now(),
      step: 1,
      isClosing: false,
      bookingPrice: null,
      servicesPrice: null,
      prepayMode: 'none',
      prepayAmount: null,
      partial: false,
      paymentTiming: 'later',
      services: requiredServices,
      ticketTariffs: {},
      playersCount: this.stateManager.getMinPlayersCount(),
      selectedTariff: null,
      tickets: slot.tickets ? { count: 1 } : null
    })

    Modals.getInstance().openModal(this.modalName)
    const zisDescription = this.modal.querySelector<HTMLElement>(
      '[data-zis-description]'
    )
    if (zisDescription) zisDescription.textContent = quest.zis_description || ''
    this.eventHandlers.autoApplyPromoCode()

    // Обновить UI
    this.uiUpdater.updateAll()

    // Обновить информацию о квесте
    this.uiUpdater.updateQuestInfo(this.modal, this.stateManager.getState())
  }

  /**
   * ОТКРЫТИЕ МОДАЛКИ для билетной системы (Quest#ticket_system) — без расписания:
   * кнопка «Забронировать» открывает попап напрямую, минуя выбор слота.
   * Синтетический слот с нейтральными значениями позволяет переиспользовать весь
   * остальной код open()/ui-updaters, рассчитанный на наличие state.slot. Дата
   * слота пустая, пока клиент не выберет день посещения в форме.
   */
  public openTicketSystem(quest: Quest): void {
    this.open(quest, {
      date: '',
      time: '00:00',
      prices: [],
      booked: false,
      tickets: null,
      min_price: 0
    })
  }

  /**
   * Закрыть бронирование
   */
  private closeBooking(): void {
    // Сбросить состояние
    this.stateManager.reset()
    this.eventHandlers.resetUserAccepted()
    this.eventHandlers.resetTouchedFields()

    // Очищаем форму сразу при прерывании/закрытии брони,
    // чтобы при повторном открытии не оставались старые значения.
    this.modal.querySelectorAll<HTMLFormElement>('form').forEach((form) => {
      form.reset()

      form
        .querySelectorAll<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >('input, textarea, select')
        .forEach((field) => {
          field.disabled = false
        })
    })

    this.resetBookingTransientUi()

    setTimeout(() => {
      this.modal
        .querySelectorAll<AccordionNode>('[data-accordion]')
        .forEach((accordion) => {
          accordion.instance?.closeAll()
        })

      this.modal
        .querySelector<HTMLElement>('[data-modal-content]')
        ?.classList.remove('is-closing')
    }, 600)
  }

  /**
   * Сбросить временные UI-состояния формы (ошибки/принятие/кнопки)
   */
  private resetBookingTransientUi(): void {
    const promoWrapper = this.modal.querySelector(
      '[data-promo-code]'
    ) as HTMLElement | null
    const certificateWrapper = this.modal.querySelector(
      '[data-certificate]'
    ) as HTMLElement | null
    const promoError = this.modal.querySelector(
      '[data-promo-error]'
    ) as HTMLElement | null
    const certificateError = this.modal.querySelector(
      '[data-certificate-error]'
    ) as HTMLElement | null
    const smsError = this.modal.querySelector(
      '[data-sms-error]'
    ) as HTMLElement | null
    const smsBlock = this.modal.querySelector(
      '[data-sms-code]'
    ) as HTMLElement | null
    const promoButton = this.modal.querySelector(
      '[data-promo-apply]'
    ) as HTMLButtonElement | null
    const certificateButton = this.modal.querySelector(
      '[data-certificate-apply]'
    ) as HTMLButtonElement | null
    const promoButtonText = this.modal.querySelector('[data-promo-button-text]')
    const certificateButtonText = this.modal.querySelector(
      '[data-certificate-button-text]'
    )

    promoWrapper?.classList.remove('error', 'accepted')
    certificateWrapper?.classList.remove('error', 'accepted')

    if (promoError) {
      promoError.textContent = ''
      promoError.style.display = 'none'
    }
    if (certificateError) {
      certificateError.textContent = ''
      certificateError.style.display = 'none'
    }
    if (smsError) {
      smsError.textContent = ''
      smsError.classList.add('is-hidden')
    }
    if (smsBlock) {
      smsBlock.classList.remove('is-shown')
      smsBlock.classList.add('is-hidden')
    }

    if (promoButtonText) {
      promoButtonText.textContent = 'Применить'
    }
    if (certificateButtonText) {
      certificateButtonText.textContent = 'Применить'
    }

    if (promoButton) {
      promoButton.disabled = true
    }
    if (certificateButton) {
      certificateButton.disabled = true
    }
  }
}

// Глобальная инициализация
declare global {
  interface Window {
    BookingModal: BookingModalController
  }
}
